import type { FC, ReactNode } from 'react'
import type { ChatMessage, ChatSide } from '@/components/generals/chat/chat-card'

import { useCallback, useEffect, useRef, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { isValidEmail } from '@/lib/format'
import { submitInquiry } from '@/lib/inquiries'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { ChatCard } from '@/components/generals/chat/chat-card'
import { ChatComposer } from '@/components/generals/chat/chat-composer'

interface IntakeQuestion {
    key: string
    label: string
    question: string
    replies: Array<string>
}

const QUESTIONS: Array<IntakeQuestion> = [
    {
        key: 'interest',
        label: 'Interest',
        question: "Hi, this goes straight to our solutions team. What brings you to Quayside today?",
        replies: ['Managed Flow', 'Pilot', 'API access', 'Not sure']
    },
    {
        key: 'freight',
        label: 'Freight',
        question: "What are you shipping, and where from and to?",
        replies: ['Containers, Asia to Europe', 'Pallets across North America', 'Air freight, worldwide', 'A bit of everything']
    },
    {
        key: 'systems',
        label: 'Systems',
        question: "Which systems need to talk to each other?",
        replies: ['ERP and carriers', 'Broker and ERP', 'Warehouse and transport', 'A customer portal']
    },
    {
        key: 'timing',
        label: 'Timing',
        question: "When do you need it live?",
        replies: ['This month', 'This quarter', 'Later this year', 'Just exploring']
    }
]

const EMAIL_PROMPT = "Last thing. What's your work email? A person will reply there."
const CONFIRMATION = "Thanks, a person will reply within one working day."

const PERSONA = {
    initials: 'QS',
    name: "Quayside solutions team",
    role: "Replies within one working day"
}

type Stage = 'asking' | 'email' | 'sending' | 'done'

/** Conversational intake: one question at a time, quick replies or free typing, then a work email. */
export const ContactIntake: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { once: true, threshold: 0.3 })
    const reduced = useReducedMotion()
    const [messages, setMessages] = useState<Array<ChatMessage>>([])
    const [typing, setTyping] = useState<ChatSide | null>(null)
    const [step, setStep] = useState(0)
    const [awaiting, setAwaiting] = useState(false)
    const [stage, setStage] = useState<Stage>('asking')
    const [draft, setDraft] = useState('')
    const [error, setError] = useState<string | null>(null)
    const answers = useRef<Record<string, string>>({})
    const timers = useRef<Array<number>>([])
    const counter = useRef(0)
    const started = useRef(false)

    useEffect(() => {
        const pending = timers.current

        return () => pending.forEach(timer => window.clearTimeout(timer))
    }, [])

    const later = useCallback((callback: () => void, milliseconds: number) => {
        timers.current.push(window.setTimeout(callback, reduced ? 0 : milliseconds))
    }, [reduced])

    const push = useCallback((side: ChatSide, text: string) => {
        counter.current += 1

        const id = `intake-${counter.current}`

        setMessages(previous => [...previous, { id, side, text }])
    }, [])

    const ask = useCallback((text: string, delay = 950) => {
        setAwaiting(false)
        later(() => setTyping('left'), 250)
        later(() => {
            setTyping(null)
            push('left', text)
            setAwaiting(true)
        }, delay)
    }, [later, push])

    useEffect(() => {
        if (!inView || started.current) return

        started.current = true
        ask(QUESTIONS[0].question, 1100)
    }, [inView, ask])

    const answer = (raw: string) => {
        const text = raw.trim()

        if (!text || !awaiting || stage !== 'asking') return

        const question = QUESTIONS[step]
        const next = step + 1

        answers.current[question.key] = text
        push('right', text)
        setDraft('')
        setStep(next)

        if (next < QUESTIONS.length) {
            ask(QUESTIONS[next].question)
            return
        }

        setStage('email')
        ask(EMAIL_PROMPT)
    }

    const submitEmail = async () => {
        const value = draft.trim()

        if (!awaiting) return

        if (!isValidEmail(value)) {
            setError('Please enter a valid work email.')
            return
        }

        setError(null)
        setStage('sending')
        setAwaiting(false)
        push('right', value)
        setDraft('')
        setTyping('left')

        try {
            await submitInquiry({
                source: 'contact-chat',
                email: value,
                topic: answers.current.interest,
                details: QUESTIONS.map(question => ({
                    question: question.question,
                    answer: answers.current[question.key] ?? ''
                }))
            })

            later(() => {
                setTyping(null)
                push('left', CONFIRMATION)
                setStage('done')
            }, 800)
        }
        catch {
            setTyping(null)
            push('left', "That didn't go through just now. Could you send your email again?")
            setStage('email')
            setAwaiting(true)
        }
    }

    const restart = () => {
        timers.current.forEach(timer => window.clearTimeout(timer))
        timers.current = []
        answers.current = {}
        setMessages([])
        setTyping(null)
        setStep(0)
        setStage('asking')
        setDraft('')
        setError(null)
        ask(QUESTIONS[0].question, 700)
    }

    const current = stage === 'asking' ? QUESTIONS[step] : undefined
    const showReplies = Boolean(current) && awaiting
    const emailStage = stage === 'email' || stage === 'sending'
    const progress = Math.min(step + 1, QUESTIONS.length + 1)

    return (
        <div ref={ref}>
            <ChatCard
                persona={PERSONA}
                docLines={['New inquiry', 'Solutions team · Rotterdam, Chicago, Singapore', stage === 'done' ? 'Sent to a person' : `Step ${progress} of ${QUESTIONS.length + 1}`]}
                docKey={`${stage}-${progress}`}
                messages={messages}
                typing={typing}
                label='Conversation with the Quayside solutions team'
                logClassName='min-h-[360px] max-h-[460px]'
            >
                {showReplies && current && (
                    <div className='mb-3 flex flex-wrap gap-2 animate-fade-soft' role='group' aria-label='Quick replies'>
                        {current.replies.map(reply => (
                            <button
                                key={reply}
                                type='button'
                                onClick={() => answer(reply)}
                                className='rounded-full border border-white/15 bg-white/[0.03] px-3.5 py-2 text-[13px] text-white/85 sm:py-1.5 transition-colors hover:border-white/35 hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60'
                            >
                                {reply}
                            </button>
                        ))}
                    </div>
                )}

                {stage === 'done' ? (
                    <div className='flex items-center justify-between gap-3 rounded-[22px] border border-white/10 bg-white/[0.03] py-1.5 pl-4 pr-1.5 sm:gap-4 sm:rounded-full sm:pl-5'>
                        <span className='text-[13px] text-white/60 sm:text-[14px]'>
                            Sent. A person will reply soon.
                        </span>

                        <button
                            type='button'
                            onClick={restart}
                            className='inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-white/[0.12] px-4 text-[13px] font-medium text-white transition-colors hover:bg-white/[0.2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60'
                        >
                            <RotateCcw aria-hidden='true' className='size-3.5' />
                            Start over
                        </button>
                    </div>
                ) : (
                    <>
                        <ChatComposer
                            value={draft}
                            onChange={value => {
                                setDraft(value)
                                if (error) setError(null)
                            }}
                            onSubmit={() => emailStage ? void submitEmail() : answer(draft)}
                            type={emailStage ? 'email' : 'text'}
                            label={emailStage ? 'Your work email' : 'Your answer'}
                            placeholder={emailStage ? 'you@company.com' : 'Type your answer...'}
                            disabled={!awaiting || stage === 'sending'}
                        />

                        {error && (
                            <p role='alert' className={cn('mt-2 pl-5 text-xs text-[#ffb3a3]')}>
                                {error}
                            </p>
                        )}
                    </>
                )}
            </ChatCard>
        </div>
    )
}
